import { _ as _extends } from '../../_rollupPluginBabelHelpers-c245b26a.js';
import { useEffect } from 'react';
import { useFloating, flip, autoUpdate } from '@floating-ui/react-dom';
import { useTheme, jsx } from '@emotion/react';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect.js';
import { Box } from '../Box/Box.js';
import { Portal } from '../Portal/Portal.js';
import '@indeed/ifl-css';
import 'react-dom';

/**
 * @deprecated The `Combobox` component will be removed in IFL 7.
 * See the IFL 7 preparation guide for more information: https://link.indeed.tech/ifl-7-prep-guide
 */

const ComboboxPopup = props => {
  const {
    children,
    hidden,
    portalContainerRef,
    sx,
    targetRef,
    ...popoverProps
  } = props;
  const {
    direction
  } = useTheme();
  const {
    x,
    y,
    refs,
    strategy,
    update
  } = useFloating({
    /**
     * Hard-coded placement for now to support `Combobox`'s use-case. As we
     * look to potentially leverage `Popup` for the `Popover`, `Tooltip`,
     * `Dropdown` and `Menu` components, generalize the use of Floating UI.
     *
     * We'll need to expose a `postion` (or `placement`) prop, possibly expose
     * an `arrow` prop (for `Tooltip`s), etc.
     */
    placement: "bottom-start",
    middleware: [flip()]
  });

  // Set up reference to the target element
  useIsomorphicLayoutEffect(() => {
    refs.setReference(targetRef.current);
  }, [refs, targetRef]);

  // Update positioning when "opened"
  useEffect(() => {
    if (!hidden) {
      update();
    }
  }, [hidden, update]);

  // Update positioning as parents change
  useEffect(() => {
    // Avoid instantiating listeners when hidden
    if (hidden || !refs.reference.current || !refs.floating.current) {
      return;
    }
    return autoUpdate(refs.reference.current, refs.floating.current, update);
  }, [hidden, refs.reference, refs.floating, update]);
  const ComboboxPopupContent = jsx(Box, _extends({
    ref: refs.setFloating,
    dir: direction,
    sx: {
      position: strategy,
      top: y ?? "",
      /**
       * `rtl-css-js` automatically converts this to `right` in RTL. Floating
       * UI plans on making some "logical" improvements to their API. Be sure
       * those changes don't conflict with `rtl-css-js` when updating.
       *
       * @see https://github.com/floating-ui/floating-ui/issues/1538
       *
       * Opportunity to use `inset-*` properties instead of `top` & `left`?
       */
      left: `${x ?? ""}px /* @noflip */`,
      display: hidden ? "none" : undefined,
      ...sx
    }
  }, popoverProps), children);
  return portalContainerRef !== null ? jsx(Portal, {
    container: portalContainerRef == null ? void 0 : portalContainerRef.current
  }, ComboboxPopupContent) : ComboboxPopupContent;
};
ComboboxPopup.displayName = "ComboboxPopup";

export { ComboboxPopup };
